import { ActivatedRoute, Router } from '@angular/router';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  ViewContainerRef,
  inject,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import { BehaviorSubject, map, take, tap, withLatestFrom } from 'rxjs';
import {
  EditorFormEntity,
  FormConfiguration,
  FormMode,
  FormSaveResult,
} from 'src/app/core/models/form.models';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { AppState } from 'src/app/core/state/app.state';
import { AuditTrailComponent } from 'src/app/shared/components/audit-trail/audit-trail.component';
import { BreadcrumbService } from 'src/app/core/services/breadcrumb.service';
import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralFormEditorComponent } from 'src/app/shared/components/general-form-editor/general-form-editor.component';
import { LoggerService } from '@services/logger.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { Principal } from 'src/app/core/models/object.models';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { UserGroup } from 'src/app/core/models/core.models';
import { UserGroupData } from 'src/app/api/services/user-group-api.service';
import { UserGroupMembersComponent } from '../../components/user-group-members/user-group-members.component';
import { injectManageUserGroupMutation } from 'src/app/api/queries/user-group.queries';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { setCurrentCompany } from 'src/app/core/state/principals.actions';
import { selectUser } from 'src/app/core/state';
import { NgClass, NgStyle, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { DynamicFormComponent } from '../../../shared/components/dynamic-form/dynamic-form.component';
import { CdkPortal } from '@angular/cdk/portal';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-editor-user-group',
  templateUrl:
    '../../../shared/components/general-form-editor/general-form-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    NgStyle,
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    CdkPortal,
    SearchInputComponent,
    FaIconComponent,
    LoadingComponent,
    SuiButtonComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class EditorUserGroupComponent
  extends GeneralFormEditorComponent
  implements AfterViewInit
{
  @Input() userGroup: UserGroup;
  @Input() isPopup: boolean;
  @Input() memberIds: Array<string> = [];

  @Output() cancelled = new EventEmitter<void>();

  companyId: string | null = null;
  userGroupId: string;
  editorFormEntityName: EditorFormEntity = EditorFormEntity.UserGroup;

  private readonly manageUserGroupMutation = injectManageUserGroupMutation();

  availableCompanies$ = new BehaviorSubject<
    Array<{ name: string; value: string }>
  >([]);

  protected destroyRef = inject(DestroyRef);

  constructor(
    protected formBuilder: FormBuilder,
    protected formService: FormsService,
    protected nomService: NomenclatureService,
    protected toastService: ToastService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected store: Store<AppState>,
    protected logger: LoggerService,
    protected breadcrumbService: BreadcrumbService,
    protected viewContainerRef: ViewContainerRef,
    private companyApi: CompanyApiService,
    private utilsDataObjectApi: UtilsDataObjectApiService,
  ) {
    super(
      formBuilder,
      formService,
      toastService,
      store,
      logger,
      router,
      activatedRoute,
      breadcrumbService,
      viewContainerRef,
    );
    this.userGroup = {
      ...this.activatedRoute.snapshot.data['group'],
    };
  }

  ngOnInit(): void {
    // * determine form mode: Edit or Create
    const hasUserGroup = Boolean(this.userGroup?.id);
    this.formMode = hasUserGroup ? FormMode.Edit : FormMode.Create;

    // * init empty userGroup for Create Mode
    if (!this.userGroup) {
      this.userGroup = {
        id: null,
        title: '',
        description: '',
        companyId: '',
        company: {
          id: '',
          name: '',
        },
        members: [],
        member_ids: [],
        lifecycle_status: 1,
      };
    }

    if (this.formMode === FormMode.Create) {
      this.utilsDataObjectApi
        .getCompanies()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map((companies) =>
            companies.map((company) => ({
              name: company.name,
              value: company.id.toString(),
            })),
          ),
          tap((companies) => {
            this.availableCompanies$.next(companies);
          }),
          withLatestFrom(this.store.select(selectUser).pipe(take(1))),
          tap(([companies, user]) => {
            // WAP-1438: default Target Company is the company the admin currently operates in - the active
            // company when working as a guest admin (Agency / Global), which is the own company at home
            const defaultCompanyId =
              user?.active_company_id ?? user?.company_id;
            const companyControl = this.formGroup?.get('companyId');
            const isAvailable = companies.some(
              (company) => company.value === defaultCompanyId,
            );
            if (companyControl && !companyControl.value && isAvailable) {
              companyControl.setValue(defaultCompanyId);
            }
          }),
        )
        .subscribe();
    } else if (this.formMode === FormMode.Edit && this.userGroup.company?.id) {
      this.companyApi
        .getCompany(this.userGroup.company.id)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap((company) => {
            this.availableCompanies$.next([
              {
                name: company.company_name,
                value: company.id,
              },
            ]);

            if (this.formGroup.get('companyId')) {
              this.formGroup.get('companyId').setValue(company.id);
            }

            this.validateAndHandleInvalidForm();
          }),
        )
        .subscribe();
    }

    // ! Initialize the Form after company data is set up
    super.ngOnInit();
    this.isPopupEditor = this.isPopup;

    // * populate Form values for Edit mode
    if (hasUserGroup) {
      this.formService.populateFormValues(this.formGroup, this.userGroup);
      this.formGroup.addControl(
        'id',
        this.formBuilder.control(this.userGroup.id),
      );
    }

    // * Always set up validation sync - but it only shows errors when validationEnabled is true
    this.setupValidationSync();

    // * Setup companyId based on Mode - Edit | Create
    if (this.formMode === FormMode.Edit && this.userGroup.company?.id) {
      this.companyId = this.userGroup.company.id;
    } else if (this.formMode === FormMode.Create) {
      this.formGroup
        .get('companyId')
        ?.valueChanges.pipe(
          tap((companyId) => {
            this.companyId = companyId;
            // WAP-1438: the Members tab is instantiated once by the dynamic form with a null company, so the
            // selected target company reaches it through the principals store (clears any selection made
            // for a previously chosen company)
            this.store.dispatch(setCurrentCompany({ companyId }));
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  protected override saveViaMutation(formData: any): Promise<FormSaveResult> {
    const userGroup: UserGroup = {
      ...this.userGroup,
      ...formData,
    };

    if (userGroup.members?.length > 0) {
      this.userGroup.members.map((member) => {
        this.memberIds.push(member.id);
      });
    }

    const transformedUserGroup: UserGroupData = {
      id: userGroup.id,
      title: userGroup.title,
      description: userGroup.description,
      member_ids: this.memberIds,
      companyId: formData.companyId, // ! use the company ID from the form
      company_id: formData.companyId,
    };
    const isCreated = !userGroup.id;

    return this.manageUserGroupMutation
      .mutateAsync({ data: transformedUserGroup, isCreate: isCreated })
      .then((saved) => ({ object: saved, isCreated }));
  }

  getMembersComponentInputs(): any {
    return {
      principals: this?.userGroup?.members,
      companyId: this.companyId,
      formMode: this.formMode, // * Pass actual mode for reliable detection
    };
  }

  // Override the abstract method from the parent to provide form configuration
  loadFormConfig(params?: any): FormConfiguration {
    return {
      tabs: [
        // * General Settings Tab
        {
          label: this.translateService.instant(
            'editor-user-group.label.general-settings',
          ),
          key: 'general-settings',
          icon: ['fas', 'cog'],
          panels: [
            {
              panelName: 'General Settings Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                // * Company field - single select for both modes
                {
                  propertyName: 'companyId',
                  propertyLabel: this.translateService.instant(
                    'editor-user-group.label.target-company',
                  ),
                  controlType: 'select',
                  validators: [Validators.required],
                  valueOptionsAsync: this.availableCompanies$,
                  additionalConfig: {
                    disabled: this.formMode === FormMode.Edit,
                  },
                },
                {
                  propertyName: 'title',
                  propertyLabel: this.translateService.instant(
                    'editor-user-group.label.group-name',
                  ),
                  controlType: 'text',
                  validators: [Validators.required],
                },
                {
                  propertyName: 'description',
                  propertyLabel: this.translateService.instant(
                    'editor-user-group.label.description',
                  ),
                  controlType: 'textarea',
                  additionalConfig: {
                    placeholder: 'Description',
                    rows: 4,
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: '',
        },
        // * Members Tab
        {
          label: this.translateService.instant(
            'editor-user-group.label.members',
          ),
          key: 'members',
          icon: ['fas', 'users'],
          panels: [
            {
              panelName: 'Members Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'members',
                  propertyLabel: this.translateService.instant(
                    'editor-user-group.label.user-groups',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: UserGroupMembersComponent,
                    inputProvider: () =>
                      this.getMembersComponentInputs.bind(this),
                    outputs: {
                      principalsChanged: (principals: Principal[]) => {
                        this.userGroup.members = principals;
                      },
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: '',
        },
        {
          label: this.translateService.instant(
            'editor-user-group.label.audit-trail',
          ),
          key: 'audit-trail',
          icon: ['fas', 'lock'],
          availableInFormModes: [FormMode.Edit],
          panels: [
            {
              panelName: 'Audit Trail Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'audit-trail',
                  propertyLabel: this.translateService.instant(
                    'editor-user-group.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: AuditTrailComponent,
                    inputs: {
                      object: this.userGroup,
                      hasOwner: false,
                    },
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: 'audit-trail',
        },
      ],
    };
  }
}
